import {
    IFilterSet,
    IFilterSetItem,
    ISampleSize,
    ISearchFilterItem,
    ISearchFilterSet,
    ISearchFilterValue
} from '@vivli/features/search/infrastructure/interface';
import {
    FilterOperatorEnum,
    SearchFilterJoinEnum,
    SearchFilterMatchEnum,
    SearchFilterValueKindEnum
} from '@vivli/features/search/infrastructure/enum';
import { IDateRange } from '@vivli/shared/infrastructure/interface';

/**
 * Translates the UI's filter sets into the wire shape the backend search API expects (DEV-1133).
 *
 * The UI shape (`IFilterSet`) is unchanged, so the filter panels, the PICO wrapper and the quick
 * lookup all keep building filters exactly as they did. Only this one module knows about the wire
 * contract, which is why the cutover touches a handful of files rather than twenty-five.
 *
 * It also fixes a real bug on the way across. The code this replaces decided a value's type with
 * two guards that each began `!value || ...`, so BOTH returned true for any falsy value: a boolean
 * filter whose value was `false` was classified as a date range, failed the inner guard, and
 * silently contributed no filter at all. The user's selection simply did nothing. Here the kind is
 * decided by a real type test and sent to the backend as an explicit tag.
 */

const isDateRange = (value: unknown): value is IDateRange => typeof value === 'object' && value !== null && 'from' in value;

const isSampleSize = (value: unknown): value is ISampleSize => typeof value === 'object' && value !== null && 'min' in value;

const toIsoString = (value: Date): string | undefined => (value ? new Date(value).toISOString() : undefined);

/**
 * The kind tag for one UI value.
 *
 * Order matters: boolean is tested first and by `typeof`, which is what keeps `false` a boolean.
 */
const getValueKind = (value: IFilterSetItem['value'], isYearRange: boolean): SearchFilterValueKindEnum => {
    if (typeof value === 'boolean') {
        return SearchFilterValueKindEnum.Boolean;
    }

    if (isDateRange(value)) {
        return isYearRange ? SearchFilterValueKindEnum.YearRange : SearchFilterValueKindEnum.DateRange;
    }

    if (isSampleSize(value)) {
        return SearchFilterValueKindEnum.NumberRange;
    }

    return SearchFilterValueKindEnum.Text;
};

const toFilterValue = (value: IFilterSetItem['value'], isYearRange: boolean): ISearchFilterValue => {
    const kind = getValueKind(value, isYearRange);

    switch (kind) {
        case SearchFilterValueKindEnum.Boolean:
            return { kind, boolean: value as boolean };
        case SearchFilterValueKindEnum.DateRange:
        case SearchFilterValueKindEnum.YearRange:
            return {
                kind,
                fromDate: toIsoString((value as IDateRange).from),
                toDate: toIsoString((value as IDateRange).to)
            };
        case SearchFilterValueKindEnum.NumberRange:
            return {
                kind,
                minNumber: (value as ISampleSize).min,
                maxNumber: (value as ISampleSize).max
            };
        default:
            return { kind, text: `${value}` };
    }
};

/**
 * How one item is matched against its field.
 *
 * Derived per item, not per set. An earlier version read the set's first item and applied that
 * match to all of them, which silently mislabelled every later item in a set whose values were not
 * all the same kind.
 *
 * `FilterOperatorEnum.Any` was never an OData operator - it was a sentinel meaning "this field is a
 * collection, use the any() form". That intent becomes `CollectionContains`. Range values get
 * `Range`, and everything else is scalar equality.
 */
const getMatch = (value: IFilterSetItem['value'], setOperator?: FilterOperatorEnum): SearchFilterMatchEnum => {
    if (setOperator === FilterOperatorEnum.Any) {
        return SearchFilterMatchEnum.CollectionContains;
    }

    if (isDateRange(value) || isSampleSize(value)) {
        return SearchFilterMatchEnum.Range;
    }

    return SearchFilterMatchEnum.Equals;
};

const toFilterItem = (item: IFilterSetItem, filterSet: IFilterSet): ISearchFilterItem => ({
    //the item's own key wins, matching the UI shape where a set key is a default its items may
    //override. PICO relies on this: its set key is a term id rather than a field name.
    field: item.key || filterSet.key,
    match: getMatch(item.value, filterSet.operator),
    value: toFilterValue(item.value, filterSet.isYearRange === true)
});

const toFilterSet = (filterSet: IFilterSet): ISearchFilterSet => ({
    itemJoin: filterSet.itemOperator === FilterOperatorEnum.Or ? SearchFilterJoinEnum.Or : SearchFilterJoinEnum.And,
    items: (filterSet.items || []).map((item) => toFilterItem(item, filterSet))
});

/**
 * Converts UI filter sets for the wire, dropping any that carry no items.
 *
 * An empty set is dropped here as well as server-side, so a request with nothing selected sends no
 * filter sets at all rather than a list of empties.
 */
export const toSearchFilterSets = (filterSets: IFilterSet[]): ISearchFilterSet[] =>
    (filterSets || []).filter((filterSet) => filterSet?.items?.length > 0).map(toFilterSet);
